import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = {};

const upsert = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
};

const setMeta = (attr, key, content) => {
  const el = upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute(attr, key);
    return m;
  });
  el.setAttribute('content', content || '');
};

const addLink = (rel, attrs) => {
  const l = document.createElement('link');
  l.rel = rel;
  Object.entries(attrs).forEach(([k, v]) => l.setAttribute(k, v));
  document.head.appendChild(l);
};

function applyMeta(meta) {
  document.title = meta.title;
  document.documentElement.lang = meta.lang;
  setMeta('name', 'description', meta.description);
  setMeta('name', 'robots', meta.robots);
  [['og:title', meta.title], ['og:description', meta.description], ['og:url', meta.canonical], ['og:image', meta.og_image], ['og:locale', meta.og_locale]]
    .forEach(([k, v]) => setMeta('property', k, v));
  [['twitter:title', meta.title], ['twitter:description', meta.description], ['twitter:image', meta.og_image]]
    .forEach(([k, v]) => setMeta('name', k, v));
  document.head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang], script[data-seo-jsonld]').forEach(el => el.remove());
  if (meta.alternates.length) addLink('canonical', { href: meta.canonical });
  meta.alternates.forEach(a => addLink('alternate', { hreflang: a.hreflang, href: a.href }));
  if (meta.jsonld) {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-seo-jsonld', '');
    s.textContent = JSON.stringify(meta.jsonld);
    document.head.appendChild(s);
  }
}

// Keeps <head> in sync during in-app navigation. The server delivers the same tags on first load.
export default function SEO() {
  const { pathname } = useLocation();

  useEffect(() => {
    let active = true;
    cache[pathname] = cache[pathname] || axios.get(`${API}/seo/meta`, { params: { path: pathname } }).then(r => r.data);
    cache[pathname].then(meta => { if (active) applyMeta(meta); }).catch(() => { delete cache[pathname]; });
    return () => { active = false; };
  }, [pathname]);

  return null;
}
